import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Truck, Snowflake, CheckCircle2, AlertTriangle, Clock, ShieldCheck,
  ClipboardCheck, XCircle, FileText, Plus, Repeat,
} from "lucide-react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import { useDialog } from "@/context/DialogProvider";
import ClosedTodayBanner from "@/components/ClosedTodayBanner";

/**
 * Daily Vehicle Cleanliness — tenant tab. Lives inside DailyTracking beside
 * "Daily Cleanliness". Lists every vehicle at the active site with today's
 * status (Missing / Pending / Signed off). Log-check modal renders the
 * bilingual template; sign-off modal locks the record forever.
 */
const todayISO = () => new Date().toISOString().slice(0, 10);
const VERIFY_ROLES = ["owner", "manager", "supervisor"];

export default function DailyVehicleCleanliness({ embedded = false }) {
  const { user } = useAuth();
  const { activeSiteId } = useSite();
  const canVerify = VERIFY_ROLES.includes(user?.role);
  const [params, setParams] = useSearchParams();

  const [vehicles, setVehicles] = useState([]);  // resolved vehicles at active site
  const [checks, setChecks] = useState([]);      // 14 days of checks
  const [loading, setLoading] = useState(true);
  const [logging, setLogging] = useState(null);   // vehicle being logged
  const [verifyingId, setVerifyingId] = useState(null); // check being verified (spinner)
  const [activeTab, setActiveTab] = useState("all"); // vehicle-id tab or "all"

  // Verify a check without asking for the operator's name — the
  // backend stamps the record with the logged-in verifier's identity so
  // the audit trail stays tamper-proof. Product-owner spec 2026-09-28:
  // "the system should know based on the user login details and
  // automatically verify the task."
  const verifyCheck = async (c) => {
    if (!c || c.status === "signed_off") return;
    setVerifyingId(c.id);
    try {
      // Empty body — backend derives the signer name from `user.name`.
      await api.post(`/vehicle-clean-checks/${c.id}/sign-off`, {});
      toast.success(`Verified ${c.equipment_name} · ${c.check_date}`);
      await load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Couldn't verify.");
    } finally { setVerifyingId(null); }
  };

  const load = async () => {
    setLoading(true);
    try {
      // Pull 14 days so the tile can render a 7-day compact strip like
      // the Daily Cleanliness facility tile, and still keep a fortnight
      // of headroom for follow-up drill-down (Phase 4).
      const since = new Date(Date.now() - 13 * 86400 * 1000).toISOString().slice(0, 10);
      const until = todayISO();
      const [eqRes, typesRes, chkRes] = await Promise.all([
        api.get("/equipment"),
        api.get("/equipment-types"),
        api.get("/vehicle-clean-checks", { params: { since, until } }),
      ]);
      const typeMap = new Map((typesRes.data.equipment_types || []).map((t) => [t.id, t]));
      const vs = (eqRes.data.equipment || [])
        .filter((e) => !e.is_archived)
        .filter((e) => (activeSiteId ? e.site_id === activeSiteId : true))
        .map((e) => ({ ...e, _type: typeMap.get(e.equipment_type_id) }))
        .filter((e) => ["delivery truck", "cold truck"].includes((e._type?.name || "").toLowerCase()));
      setVehicles(vs);
      setChecks(chkRes.data.checks || []);
    } catch { /* toast handled inline */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [activeSiteId]);

  // QR deep-link: /app/daily-tracking?tab=vehicles&scope=equipment&equipmentId=<id>
  // → auto-open the log-check modal on that vehicle so drivers can hit
  // Submit in one tap. Strip the equipmentId param after so refreshes
  // don't re-fire the modal.
  useEffect(() => {
    const scope = params.get("scope");
    const equipmentId = params.get("equipmentId");
    if (scope !== "equipment" || !equipmentId || vehicles.length === 0) return;
    const v = vehicles.find((x) => x.id === equipmentId);
    if (v) {
      setLogging(v);
      const next = new URLSearchParams(params);
      next.delete("scope"); next.delete("equipmentId");
      setParams(next, { replace: true });
    }
  }, [vehicles]);

  const today = todayISO();
  const todayCountByEq = useMemo(() => {
    // How many checks were submitted today per vehicle — matches Daily
    // Cleanliness which shows "N today" when multiple entries exist.
    const m = new Map();
    for (const c of checks) {
      if (c.check_date === today) m.set(c.equipment_id, (m.get(c.equipment_id) || 0) + 1);
    }
    return m;
  }, [checks, today]);
  const historyByEq = useMemo(() => {
    // Map: equipment_id -> { date: { has_issue, status } } — used to
    // draw the 7-day mini strip on each vehicle tile.
    const m = new Map();
    for (const c of checks) {
      if (!m.has(c.equipment_id)) m.set(c.equipment_id, {});
      m.get(c.equipment_id)[c.check_date] = { has_issue: !!c.has_issue, status: c.status };
    }
    return m;
  }, [checks]);
  const checkByEq = useMemo(() => {
    // Latest today's check per vehicle — sorted by submitted_at desc.
    const m = new Map();
    for (const c of checks) {
      if (c.check_date !== today) continue;
      const prev = m.get(c.equipment_id);
      if (!prev || (c.submitted_at || "") > (prev.submitted_at || "")) {
        m.set(c.equipment_id, c);
      }
    }
    return m;
  }, [checks, today]);

  return (
    <div className="vb-fade-in" data-testid="daily-vehicle-cleanliness-page">
      {!embedded && (
        <header className="mb-4">
          <h1 className="vb-heading text-3xl m-0 mb-1" style={{ color: "var(--vb-text-2)" }}>
            <Truck size={24} className="inline-block mr-2 -mt-1" />
            Daily Vehicle Cleanliness
          </h1>
          <p className="text-sm opacity-70 m-0">
            Log today&apos;s cleanliness check for each vehicle. Supervisors verify to lock the record.
          </p>
        </header>
      )}

      <ClosedTodayBanner siteId={activeSiteId} />

      {/* Monthly report has moved to Reports + Audit Vault (same lifecycle
          as Daily Hygiene / Daily Cleanliness). Keep this row as a
          contextual pointer instead of a one-off draft download. */}
      {activeSiteId && vehicles.length > 0 && (
        <div className="text-[11px] opacity-70 mb-3 flex items-center gap-1" data-testid="dvc-vault-hint">
          <FileText size={12} />
          <span>Monthly report is filed under <strong>Reports</strong> &amp; <strong>Audit Vault</strong> → Daily Vehicle Cleanliness.</span>
        </div>
      )}

      {loading ? (
        <div className="vb-card p-6 opacity-60" data-testid="dvc-loading">Loading…</div>
      ) : vehicles.length === 0 ? (
        <div className="vb-card p-6 opacity-70" data-testid="dvc-empty">
          No vehicles for this site yet. Add a Delivery Truck or Cold Truck under <strong>Equipment</strong> to see it here.
        </div>
      ) : (
        <>
          {/* Per-vehicle tabs — mirror the Daily Cleanliness page's
              per-facility tab pattern so supervisors focus on one truck
              at a time and see its full pending-sign-off backlog. */}
          {vehicles.length > 1 && (
            <div className="flex gap-1 mb-3 overflow-x-auto" data-testid="dvc-tabs" style={{ scrollbarWidth: "thin" }}>
              <button className={`vb-btn sm ${activeTab === "all" ? "" : "ghost"}`}
                onClick={() => setActiveTab("all")}
                data-testid="dvc-tab-all"
                style={{ whiteSpace: "nowrap", flex: "0 0 auto" }}>
                All · {vehicles.length}
              </button>
              {vehicles.map((v) => {
                const c = checkByEq.get(v.id);
                const pending = c?.status === "pending";
                return (
                  <button key={v.id}
                    className={`vb-btn sm ${activeTab === v.id ? "" : "ghost"}`}
                    onClick={() => setActiveTab(v.id)}
                    data-testid={`dvc-tab-${v.id}`}
                    style={{ whiteSpace: "nowrap", flex: "0 0 auto" }}>
                    {v.name}
                    {pending && <span className="vb-pill warn text-[9px] ml-1" style={{ padding: "1px 6px" }}>pending</span>}
                  </button>
                );
              })}
            </div>
          )}
          <div className="space-y-2">
            {vehicles
              .filter((v) => activeTab === "all" || v.id === activeTab)
              .map((v) => (
                <VehicleRow
                  key={v.id}
                  vehicle={v}
                  check={checkByEq.get(v.id) || null}
                  todayCount={todayCountByEq.get(v.id) || 0}
                  history={historyByEq.get(v.id) || {}}
                  allChecks={checks.filter((c) => c.equipment_id === v.id)}
                  canVerify={canVerify}
                  verifyingId={verifyingId}
                  onLog={() => setLogging(v)}
                  onVerify={verifyCheck}
                />
              ))}
          </div>
        </>
      )}

      {logging && (
        <LogCheckModal
          vehicle={logging}
          onClose={() => setLogging(null)}
          onSaved={() => { setLogging(null); load(); }}
        />
      )}
    </div>
  );
}

function VehicleRow({ vehicle, check, todayCount, history, allChecks, canVerify, verifyingId, onLog, onVerify }) {
  const isCold = (vehicle._type?.name || "").toLowerCase() === "cold truck";
  const status = check?.status;
  const today = todayISO();
  const [expandedId, setExpandedId] = useState(null);
  // Pending-sign-off backlog for this vehicle — every check inside the
  // 14-day window that hasn't been signed off yet. Mirrors the
  // "unverified" list Daily Cleanliness shows under each facility.
  const pending = (allChecks || []).filter((c) => c.status === "pending");

  // 7-day compact strip (oldest → today, like Daily Cleanliness). Any
  // day with a check counts toward the streak; a day with an issue is
  // still logged but rendered with the warn colour.
  const days = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(Date.now() - i * 86400 * 1000).toISOString().slice(0, 10);
    const h = history?.[d];
    days.push({ date: d, kind: h ? (h.has_issue ? "fail" : "pass") : "miss" });
  }
  let streak = 0;
  for (let i = days.length - 1; i >= 0; i--) {
    if (days[i].kind === "pass" || days[i].kind === "fail") streak++;
    else break;
  }
  const compliance = (() => {
    const captured = days.filter((d) => d.kind !== "miss").length;
    return Math.round((captured / days.length) * 100);
  })();

  return (
    <div className="vb-card p-3" data-testid={`dvc-row-${vehicle.id}`}>
      <div className="flex items-center gap-3 flex-wrap">
        {isCold
          ? <Snowflake size={18} style={{ color: "#2563eb", flexShrink: 0 }} />
          : <Truck size={18} style={{ color: "var(--vb-accent)", flexShrink: 0 }} />}
        <div className="flex-1 min-w-[160px]">
          <div className="text-sm font-medium" style={{ color: "var(--vb-text-2)" }}>{vehicle.name}</div>
          <div className="text-[11px] opacity-60">
            {vehicle._type?.name || "Vehicle"}
            {vehicle.serial_number && <> · <span title="Vehicle number">{vehicle.serial_number}</span></>}
          </div>
        </div>

        {/* Today's status pill */}
        {status === "signed_off" && (
          <span className="vb-pill ok text-[10px]" data-testid={`dvc-status-signed-${vehicle.id}`}>
            <ShieldCheck size={10} /> Signed off today
          </span>
        )}
        {status === "pending" && (
          <span className="vb-pill warn text-[10px]" data-testid={`dvc-status-pending-${vehicle.id}`}>
            <Clock size={10} /> Pending sign-off
          </span>
        )}
        {!status && (
          <span className="vb-pill err text-[10px]" data-testid={`dvc-status-missing-${vehicle.id}`}>
            <XCircle size={10} /> Missing today
          </span>
        )}
        {check?.has_issue && (
          <span className="vb-pill err text-[10px]" title={check.corrective_action || "Issue flagged"}>
            <AlertTriangle size={10} /> Issue flagged
          </span>
        )}
        {todayCount > 1 && (
          <span className="vb-pill text-[10px]" title="Multiple checks logged today"
            data-testid={`dvc-multi-${vehicle.id}`}>
            <Repeat size={10} /> {todayCount} today
          </span>
        )}

        {/* 7-day compact strip */}
        <div className="flex gap-0.5" data-testid={`dvc-strip-${vehicle.id}`} title="Last 7 days">
          {days.map((d) => (
            <span key={d.date}
              className={`vb-streak-dot vb-streak-${d.kind}`}
              title={`${d.date}${d.kind === "pass" ? " · all pass" : d.kind === "fail" ? " · issue" : " · missing"}`} />
          ))}
        </div>

        <div className="text-right" style={{ minWidth: 90 }}>
          <div className="text-[10px] uppercase tracking-wider opacity-60">7d streak</div>
          <div className="text-sm font-medium" style={{ color: streak > 0 ? "var(--vb-accent)" : undefined }}>
            {streak}d · {compliance}%
          </div>
        </div>

        <button className="vb-btn text-sm" onClick={onLog}
          data-testid={`dvc-log-${vehicle.id}`}
          title="Log a new cleanliness check">
          <Plus size={13} /> Log check
        </button>
        {canVerify && check?.status === "pending" && (
          <button className="vb-btn ghost text-sm" onClick={() => onVerify(check)}
            disabled={verifyingId === check.id}
            data-testid={`dvc-verify-${vehicle.id}`}>
            <CheckCircle2 size={13} /> {verifyingId === check.id ? "Verifying…" : "Verify"}
          </button>
        )}
      </div>
      {check && (
        <div className="text-[11px] opacity-60 mt-2 pl-6">
          {check.submitted_by || "—"} · {new Date(check.submitted_at).toLocaleString()}
          {check.signed_off_by && (
            <> · verified by {check.signed_off_by} · {new Date(check.signed_off_at).toLocaleString()}</>
          )}
        </div>
      )}
      {pending.length > 0 && (
        <div className="mt-3 pt-3 border-t" style={{ borderColor: "var(--vb-border)" }}
          data-testid={`dvc-pending-panel-${vehicle.id}`}>
          <div className="text-[11px] uppercase tracking-wider opacity-70 font-semibold mb-2">
            Pending verification · {pending.length}
          </div>
          <div className="space-y-1">
            {pending.map((c) => {
              const open = expandedId === c.id;
              return (
                <div key={c.id} className="rounded" style={{ background: "var(--vb-bg-2,#faf6f1)", border: "1px solid var(--vb-border)" }}
                  data-testid={`dvc-pending-row-${c.id}`}>
                  <div className="flex items-center gap-2 flex-wrap p-2">
                    <button className="vb-btn ghost sm" onClick={() => setExpandedId(open ? null : c.id)}
                      data-testid={`dvc-pending-toggle-${c.id}`}
                      style={{ padding: "2px 6px" }}
                      aria-label={open ? "Collapse" : "Expand"}>
                      {open ? "▾" : "▸"}
                    </button>
                    <span className={`vb-pill ${c.has_issue ? "err" : "ok"} text-[10px]`}>
                      {c.has_issue ? "Issue flagged" : "All pass"}
                    </span>
                    <span className="vb-pill warn text-[10px]">Unverified</span>
                    <span className="text-[12px] opacity-70">{c.check_date} · {c.submitted_by || "—"}</span>
                    <div className="ml-auto flex gap-2">
                      {canVerify && (
                        <button className="vb-btn sm" onClick={() => onVerify(c)}
                          disabled={verifyingId === c.id}
                          data-testid={`dvc-pending-verify-${c.id}`}>
                          <ShieldCheck size={12} /> {verifyingId === c.id ? "Verifying…" : "Verify"}
                        </button>
                      )}
                    </div>
                  </div>
                  {open && (
                    <div className="px-3 pb-2 text-[12px]" data-testid={`dvc-pending-details-${c.id}`}>
                      <div className="opacity-70 mb-1">
                        Submitted {new Date(c.submitted_at).toLocaleString()}
                      </div>
                      <ul className="m-0 pl-4 space-y-0.5">
                        {(c.items || []).map((it) => (
                          <li key={it.item_key} style={{ color: it.ok ? undefined : "#991b1b" }}>
                            <strong>{it.ok ? "✓" : "✗"}</strong> {it.label}
                            {!it.ok && it.reason && <span className="opacity-80"> — {it.reason}</span>}
                          </li>
                        ))}
                      </ul>
                      {c.corrective_action && (
                        <div className="mt-1"><strong>Corrective:</strong> {c.corrective_action}</div>
                      )}
                      {c.notes && <div className="mt-1 opacity-70"><strong>Notes:</strong> {c.notes}</div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function LogCheckModal({ vehicle, onClose, onSaved }) {
  const [tpl, setTpl] = useState(null);
  const [answers, setAnswers] = useState({});   // { item_key: { ok, reason } }
  const [corrective, setCorrective] = useState("");
  const [notes, setNotes] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/vehicle-clean-templates/${vehicle.id}`);
        setTpl(data);
        // Preselect every item OK — matches Daily Cleanliness. Drivers
        // only toggle Not-OK when there's something to flag.
        const seed = {};
        for (const s of data.sections || []) {
          for (const it of s.items || []) {
            seed[it.key] = { ok: true, reason: "" };
          }
        }
        setAnswers(seed);
      } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Failed to load template."); }
    })();
  }, [vehicle.id]);

  const items = (tpl?.sections || []).flatMap((s) => s.items || []);
  const hasIssue = Object.values(answers).some((a) => a && a.ok === false);
  const canSubmit = !busy && items.length > 0 && items.every((it) => {
    const a = answers[it.key];
    if (!a) return false;
    if (a.ok === false && !(a.reason || "").trim()) return false;
    return true;
  }) && (!hasIssue || corrective.trim().length > 0);

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      await api.post("/vehicle-clean-checks", {
        equipment_id: vehicle.id,
        check_date: todayISO(),
        items: items.map((it) => ({
          item_key: it.key,
          ok: !!answers[it.key]?.ok,
          reason: (answers[it.key]?.reason || "").trim(),
        })),
        corrective_action: corrective.trim(),
        notes: notes.trim(),
      });
      toast.success("Check logged");
      onSaved();
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "Failed to save");
    } finally { setBusy(false); }
  };

  return createPortal(
    <div className="vb-modal-bg" onClick={onClose} data-testid="dvc-log-modal">
      <div className="vb-modal" onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 640, maxHeight: "90vh", overflowY: "auto" }}>
        <div className="flex items-start justify-between gap-2 mb-3">
          <div>
            <h2 className="vb-heading text-xl m-0" style={{ color: "var(--vb-text-2)" }}>{vehicle.name}</h2>
            <div className="text-[12px] opacity-70">
              {vehicle._type?.name} · {tpl?.form_ref}
            </div>
          </div>
        </div>

        {err && <div className="vb-pill err mb-3" data-testid="dvc-log-error">{err}</div>}

        {!tpl ? (
          <div className="opacity-60 py-4">Loading checklist…</div>
        ) : (
          <>
            <div className="space-y-2 mb-3" data-testid="dvc-items">
              {items.map((it) => (
                <ItemRow key={it.key} item={it}
                  value={answers[it.key]}
                  onChange={(patch) => setAnswers((p) => ({ ...p, [it.key]: { ...p[it.key], ...patch } }))} />
              ))}
            </div>

            {hasIssue && (
              <div className="mb-3 p-3 rounded"
                style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.4)" }}>
                <label className="block text-[12px] font-semibold mb-1" style={{ color: "#991b1b" }}>
                  <AlertTriangle size={12} className="inline -mt-0.5 mr-1" /> Corrective action <span style={{ color: "#b91c1c" }}>*</span>
                </label>
                <textarea className="vb-input" rows={2} value={corrective}
                  onChange={(e) => setCorrective(e.target.value)} data-testid="dvc-corrective"
                  placeholder="e.g. cleaned interior + reported pest smell to supervisor" />
              </div>
            )}

            <label className="block text-[11px] opacity-70 mb-1">Notes (optional)</label>
            <textarea className="vb-input" rows={2} value={notes}
              onChange={(e) => setNotes(e.target.value)} data-testid="dvc-notes" />

            <div className="flex justify-end gap-2 mt-3">
              <button className="vb-btn ghost" onClick={onClose}>Close</button>
              <button className="vb-btn" onClick={submit} disabled={!canSubmit}
                data-testid="dvc-submit">
                {busy ? "Saving…" : "Submit check"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

function ItemRow({ item, value, onChange }) {
  return (
    <div className="p-2 rounded" style={{ background: "var(--vb-bg-2, #faf6f1)", border: "1px solid var(--vb-border)" }}
      data-testid={`dvc-item-${item.key}`}>
      <div className="text-[13px] font-medium">{item.label}</div>
      {item.label_zh && <div className="text-[11px] opacity-60">{item.label_zh}</div>}
      <div className="flex gap-2 mt-2">
        {[{ v: true, label: "OK", zh: "合格" }, { v: false, label: "Not OK", zh: "不合格" }].map((opt) => {
          const on = value?.ok === opt.v;
          return (
            <button key={String(opt.v)} type="button"
              onClick={() => onChange({ ok: opt.v })}
              data-testid={`dvc-answer-${item.key}-${opt.v ? "ok" : "not-ok"}`}
              style={{
                padding: "4px 12px", borderRadius: 6, fontWeight: 600, fontSize: 12,
                background: on ? (opt.v ? "#0f766e" : "#b91c1c") : "white",
                color: on ? "white" : "#0f172a",
                border: `1px solid ${on ? "transparent" : "#cbd5e1"}`,
                cursor: "pointer",
              }}>
              {opt.label} <span style={{ opacity: 0.7, fontWeight: 400 }}>· {opt.zh}</span>
            </button>
          );
        })}
      </div>
      {value?.ok === false && (
        <input className="vb-input mt-2 text-[12px]" placeholder="Describe the issue (required)"
          value={value?.reason || ""}
          onChange={(e) => onChange({ reason: e.target.value })}
          data-testid={`dvc-reason-${item.key}`} />
      )}
    </div>
  );
}
